import React, { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { BodySmall, BodySmallMidEmphasis } from '@/design-system';
import { GET_USER_FAVORITE_ORGS } from '@/graphql/queries';
import type { FavoriteOrg, GetUserFavoriteOrgsResponse } from '@/graphql/types';
import colors from '@/theme/colors';
import { getInitials } from '@/utils/stringUtils';
import { LoadingSpinner } from '@/components';
import { OrganizationCard, type Organization } from './components/OrganizationCard';
import { EmptyState } from './components/EmptyState';
import styles from './FollowingOrganizations.styles';

const MOCK_AVATAR_COLORS = [
  colors.peach200,
  colors.orange100,
  colors.mint,
  colors.peach300,
  colors.prussian200,
];

const mapFavoriteOrgToOrganization = (org: FavoriteOrg): Organization => {
  const hasNewFlyers = org.newUnreadFlyersCount > 0;
  return {
    id: String(org.orgId),
    orgId: Number(org.orgId),
    name: org.name,
    initials: getInitials(org.name),
    avatarColor: MOCK_AVATAR_COLORS[org.orgId % MOCK_AVATAR_COLORS.length],
    address: org.address,
    flyerCount: hasNewFlyers ? org.newUnreadFlyersCount : org.activeFlyersCount,
    hasNewFlyers,
    approvedFlyerCount: org.approvedFlyerCount,
    activeFlyersCount: org.activeFlyersCount,
    isFollowing: org.isFollowing,
  };
};

export const FollowingOrganizations: React.FC = () => {
  const { data, loading } = useQuery<GetUserFavoriteOrgsResponse>(GET_USER_FAVORITE_ORGS, {
    fetchPolicy: 'network-only',
  });

  const organizations = useMemo(
    () => data?.getUserFavoriteOrgs?.orgs?.map(mapFavoriteOrgToOrganization) ?? [],
    [data]
  );

  const isInactiveOrg = useCallback(
    (org: Organization): boolean => org.approvedFlyerCount === 0 && org.flyerCount === 0,
    [organizations]
  );

  const inactiveCount = useMemo(() => organizations.filter(isInactiveOrg).length, [organizations]);

  const activeCount = organizations.length - inactiveCount;

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className={styles.horizontalPadding}>
        {inactiveCount === 0 ? (
          <BodySmall color={colors.slate}>
            {organizations.length} {organizations.length === 1 ? 'organization' : 'organizations'}
          </BodySmall>
        ) : (
          <BodySmall color={colors.slate}>
            <BodySmallMidEmphasis as="span" color={colors.jungle}>
              {activeCount} active
            </BodySmallMidEmphasis>
            {` • ${inactiveCount} inactive`}
          </BodySmall>
        )}
      </div>
      {organizations.length === 0 ? (
        <EmptyState />
      ) : (
        <ul className={styles.list} aria-label="Following organizations">
          {organizations.map(org => (
            <OrganizationCard key={org.id} organization={org} isInactive={isInactiveOrg(org)} />
          ))}
        </ul>
      )}
    </div>
  );
};
